/**
 * Theme tokens -> CSS custom properties, plus the print stylesheet.
 *
 * B-1's instruction is a faithful *reconstruction*, not pixel-matching: the source
 * PDFs are raster at 130 ppi and no design files exist (D29). So the type scale,
 * colour and spacing are derived into tokens here rather than measured to the pixel.
 */

import type { ThemeTokens } from '@pdmg/reporting-schema';
import { REPORT_PAGE_BACKGROUND } from './report-background.js';

/*
 * Contrast guards for white-label colours.
 *
 * An agency's brand colour becomes the theme primary, and the primary colours every label and figure on
 * white plus the white text on the card-title bars. A light brand colour (the FBI's lime #a7e548) put
 * lime text on white and white text on lime — both about 1.5:1, unreadable. So text uses the brand colour
 * only when it is dark enough to read, and the bars switch to dark text when the brand is too light.
 * Non-hex values (none today) pass through untouched.
 */
function rgbOf(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}
function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
function contrastWithWhite(hex: string): number | null {
  const rgb = rgbOf(hex);
  return rgb ? 1.05 / (luminance(rgb) + 0.05) : null;
}
const toHex = (rgb: number[]) => '#' + rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');

/** The colour for text on white: the primary if it reads (WCAG 4.5:1), else the accent, else the primary darkened until it does. */
export function readableTextColor(primary: string, accent?: string): string {
  const ok = (c?: string) => !!c && (contrastWithWhite(c) ?? 99) >= 4.5;
  if (ok(primary)) return primary;
  if (ok(accent)) return accent!;
  const rgb = rgbOf(primary);
  if (!rgb) return primary;
  for (let k = 0.9; k > 0; k -= 0.05) {
    const c = toHex(rgb.map((v) => v * k));
    if (ok(c)) return c;
  }
  return '#0A0A0A';
}

/** Text on a fill of `fill`: the inverse (white) when it reads at 3:1 (bold, small caps), else dark. */
export function textOnFill(fill: string, inverse: string): string {
  const c = contrastWithWhite(fill);
  return c === null || c >= 3 ? inverse : '#0A0A0A';
}

export function themeToCssVars(theme: ThemeTokens): string {
  const t = theme.typography;
  // Modular scale off the base size, so every step is derived rather than authored.
  const step = (n: number) => (t.baseSizePt * Math.pow(t.scale, n)).toFixed(2);
  /*
   * `:root, :host` — both, and the second is not decorative.
   *
   * `:root` matches the document's root element, which is outside a shadow boundary. The
   * builder renders this document inside a shadow root for style isolation, and there
   * every one of these custom properties resolved to nothing: card headers lost their
   * fill, values lost their colour, and the preview quietly stopped matching the PDF it
   * is supposed to be a preview OF. `:host` is the shadow tree's equivalent, and naming
   * both makes one stylesheet correct in both places.
   */
  return `
:root, :host {
  --color-primary: ${theme.color.primary};
  --color-accent: ${theme.color.accent};
  --color-icon: ${theme.color.iconColor ?? theme.color.primary};
  --color-corner-dot: ${theme.color.cornerDot ?? '#9FB6DE'};
  --color-header-glow: ${theme.color.headerGlow ?? '#2E74D6'};
  --color-surface: ${theme.color.surface};
  --color-surface-muted: ${theme.color.surfaceMuted};
  --color-border: ${theme.color.border};
  --color-text: ${theme.color.textPrimary};
  --color-text-inverse: ${theme.color.textInverse};
  --color-header-from: ${theme.color.headerFrom ?? theme.color.primary};
  --color-header-to: ${theme.color.headerTo ?? theme.color.primary};
  --color-header-deep: ${theme.color.headerDeep ?? '#002E86'};

  --font-family: ${JSON.stringify(t.fontFamily)}, "Helvetica Neue", Arial, sans-serif;
  --size-xs: ${step(-1)}pt;
  --size-sm: ${t.baseSizePt.toFixed(2)}pt;
  --size-md: ${step(1)}pt;
  --size-lg: ${step(2)}pt;
  --size-xl: ${step(3)}pt;

  --page-width: ${theme.page.widthIn}in;
  --page-height: ${theme.page.heightIn}in;
  --page-margin: ${theme.page.marginIn}in;

  --gap: 0.12in;
  --radius: 0.055in;

  /*
   * The page is GREY and the cards are white.
   *
   * The first build made both white, which flattened every card into the page and lost
   * the mockup's most basic structural cue — you read it as one sheet of content rather
   * than a set of panels.
   */
  --color-page: ${theme.color.page ?? '#F1F3F6'};
  --color-card-head: ${theme.color.primary};
  --color-strip-border: ${theme.color.stripBorder ?? '#2B5FA8'};
  --color-value: ${readableTextColor(theme.color.primary, theme.color.accent)};
  --color-card-head-text: ${textOnFill(theme.color.primary, theme.color.textInverse)};

  /* A slight drop shadow for every panel/box — lifts the white cards off the grey page. A SINGLE,
     soft, low-offset shadow: layered/large-offset shadows rasterise inconsistently across PDF viewers
     (off-centre on Windows, hard-edged boxes on some mobile readers). This degrades gracefully. */
  --shadow: 0 1px 2px rgba(0, 19, 69, 0.14);
  /* Equal-height floor for the four breakdown panels. Per-report via the theme so a roomy display
     page can match its tall channel-icon row while the dense audio summary stays within its page. */
  --breakdown-min-height: ${theme.layout?.breakdownMinHeightIn ? `${theme.layout.breakdownMinHeightIn}in` : '1.55in'};
  /* The V2 page background art (soft waves), opted into per template. Inlined — the print fetches
     nothing — and declared once here, so every page shares one copy. 'none' keeps the flat page. */
  --page-bg-image: ${theme.layout?.pageBackground === 'waves' ? `url("${REPORT_PAGE_BACKGROUND}")` : 'none'};
  /* The wave art replaces the bottom-left dot accent, so the dots are hidden on those pages. */
  --corner-dots: ${theme.layout?.pageBackground === 'waves' ? 'none' : 'block'};
  /* Extra breathing room above the summary rows (KPIs, contract strip, the two breakdown rows). */
  --summary-row-space: ${theme.layout?.summaryRowSpaceIn ?? 0}in;
  /* Space between the header band and the Campaign Details box on the details pages. */
  --details-top-space: ${theme.layout?.detailsTopSpaceIn ?? 0}in;
  /* Extra room above the audio summary's listening / demographics / Music Genres rows. */
  --audio-row-space: ${theme.layout?.audioRowSpaceIn ?? 0}in;
}`.trim();
}

/**
 * Print stylesheet.
 *
 * `@page { margin: 0 }` matters: Playwright draws the header and footer templates
 * into the page margin box, so the document's own content must not claim it.
 */
export const PRINT_CSS = `
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: var(--font-family);
  font-size: var(--size-sm);
  color: var(--color-text);
  background: var(--color-page);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
@page { size: var(--page-width) var(--page-height); margin: 0; }

/* The page box is padding-free so the header band can bleed to the sheet edge; the
   body carries the type margins instead. */
.page {
  position: relative;
  width: var(--page-width);
  height: var(--page-height);
  display: flex;
  flex-direction: column;
  /* Flat page colour, with the optional V2 wave art laid over it, anchored to the bottom so the
     waves sit low on the sheet. The corner dots (.page::before) draw on top of both. */
  background-color: var(--color-page);
  background-image: var(--page-bg-image);
  background-position: center bottom;
  background-size: cover;
  background-repeat: no-repeat;
  break-after: page;
  page-break-after: always;
  overflow: hidden;
}
.page:last-child { break-after: auto; page-break-after: auto; }
/*
 * The mockups' bottom-left corner accent: a right-triangle halftone of dots, densest at the corner
 * and fading up and to the right. A masked dot-grid rather than a big inline SVG — the diagonal mask
 * gives the triangle. Behind the content and clear of the centred footer text.
 */
.page::before {
  content: "";
  position: absolute;
  left: 0; bottom: 0;
  width: 2in; height: 1.2in;
  background-image: radial-gradient(var(--color-corner-dot) 22%, transparent 26%);
  background-size: 0.108in 0.108in;
  background-position: bottom left;
  -webkit-mask-image: linear-gradient(41deg, #000 14%, rgba(0,0,0,0.55) 40%, transparent 60%);
  mask-image: linear-gradient(41deg, #000 14%, rgba(0,0,0,0.55) 40%, transparent 60%);
  pointer-events: none;
  z-index: 0;
  /* Off on pages carrying the V2 wave background (the art replaces the dot accent); on elsewhere. */
  display: var(--corner-dots, block);
}
.page-body, .hdr, .page-foot { position: relative; z-index: 1; }
.page-body { flex: 1 1 auto; padding: 0.26in 0.5in; min-height: 0; }
.page-foot { flex: 0 0 auto; padding: 0 0.5in 0.28in; }

.stack { display: flex; flex-direction: column; gap: var(--gap); }
.row { display: flex; gap: var(--gap); align-items: stretch; }
.row > * { flex: 1 1 0; min-width: 0; }

.card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  box-shadow: var(--shadow);
  overflow: hidden;
  /* Column flow so a body can grow to fill a card stretched to its row's height (e.g. the browser
     card centring). Identical stacking to block flow for every other card. */
  display: flex; flex-direction: column;
}
/* The breakdown cards are framed in their heading colour, as the mockup draws them. */
.card-framed { border-color: var(--color-card-head); }
/*
 * The four summary breakdown panels sit in two separate 2-column rows, so the left/right cards
 * equalize per row but the two rows did not match each other. A shared min-height makes all four read
 * as equal-height panels; it is kept modest so the densest fixed summary page still fits.
 */
.breakdown-card { min-height: var(--breakdown-min-height); }
.card-title {
  background: var(--color-card-head);
  color: var(--color-card-head-text, var(--color-text-inverse));
  font-size: var(--size-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.075in 0.13in;
  /* The mockup pairs a small glyph with every card heading. */
  display: flex; align-items: center; gap: 0.08in;
}
.card-title svg { flex: 0 0 auto; opacity: 0.95; }
/* A heading that is a proper title rather than a label is set in mixed case. */
.card-title.titlecase { text-transform: none; letter-spacing: 0.01em; font-size: var(--size-md); }
/* The right-aligned label in a card heading — e.g. the ad group name on campaign details. */
.card-title-right { margin-left: auto; font-weight: 600; text-transform: none; letter-spacing: 0.01em; font-size: var(--size-sm); }
/* The audience block's lead line — blue, uppercase, as the mockup sets it. */
.audience-heading {
  color: var(--color-primary); text-transform: uppercase; font-weight: 700;
  letter-spacing: 0.02em; font-size: var(--size-xs); margin-bottom: 0.08in;
}
/* The campaign / flight name leads the audience section; the rest read as bullets. */
.audience-lead { color: var(--color-value); font-weight: 700; font-size: var(--size-md); margin-bottom: 0.05in; }
.audience-bullets { margin: 0; padding-left: 0.2in; }
.audience-bullets li { margin: 0.025in 0; line-height: 1.3; }
.audience-bullets strong { color: var(--color-value); }
.card-body { padding: 0.13in; }

.muted { color: #5A6472; }
.tiny { font-size: var(--size-xs); }
.num { font-variant-numeric: tabular-nums; }

/* Block: PageHeader */
.hdr {
  display: flex; justify-content: space-between; align-items: flex-end;
  /* The mockup's header: a near-black-to-blue band with a soft blue glow arcing in from the upper
     right, behind the client/campaign-ID block. The radial layer sits over the base gradient. */
  background:
    radial-gradient(75% 340% at 70% -70%, var(--color-header-glow) 0%, rgba(46,116,214,0) 55%),
    linear-gradient(96deg, var(--color-header-from) 0%, var(--color-header-to) 62%, var(--color-header-deep) 100%);
  color: var(--color-text-inverse);
  padding: 0.22in 0.5in;
}
/* White-label agency logo, top-left in the header band, above the title. Height-capped so any logo
   aspect ratio sits on the band without crowding the title; width follows. */
.hdr-logo { display: block; height: 0.4in; width: auto; max-width: 3in; margin-bottom: 0.09in; object-fit: contain; }
.hdr-title { font-size: var(--size-xl); font-weight: 700; letter-spacing: -0.01em; }
.hdr-rule { height: 3px; width: 0.78in; background: var(--color-accent); margin-top: 0.07in; }
.hdr-meta { text-align: right; font-size: var(--size-sm); line-height: 1.55; }
/* The ID is the accent colour; its "CAMPAIGN ID#:" label stays white, as the mockup has it. */
.hdr-meta .cid { color: var(--color-accent); font-weight: 700; }
.hdr-meta .cid .cid-label { color: var(--color-text-inverse); }

/*
 * The footer is CENTRED, with a bullet between the notice and the page number.
 *
 * Page numbers and the year are engine-computed and deliberately not copied from the
 * mockups: audio numbers four pages as "1 of 3, 2 of 3, 2 of 3, 3 of 3", and DOOH omits
 * them on two pages then claims "of 6" across four. Matching the design must not mean
 * inheriting its arithmetic.
 */
/* Regular weight and a step smaller than the body's smallest text — the footer is fine print. */
.page-foot { text-align: center; font-size: 7.5pt; color: #6A727E; font-weight: 400; }
.page-foot .sep { margin: 0 0.06in; }
/* Footnotes under a block (the CTR basis, the device-details sum) — fine print, the footer's size. */
.footnote { font-size: 7.5pt; color: #5A6472; font-weight: 400; }

/* Block: KpiTileRow — label, then icon, then value (the mockup's order) */
.kpi { display: grid; gap: var(--gap); }
/* A section the reviewer left on that has no data to show — an honest note, not an empty card. */
.muted-note { color: #6B7480; font-style: italic; font-size: var(--size-sm); padding: 0.05in 0.02in; }
/* Badgeless trio (email rates): single-line labels, no badge — drop the two-line label box and
   tighten padding so two stacked trios fit the fixed summary sheet. */
.kpi-compact .kpi-label { min-height: 0; }
.kpi-compact .kpi-tile { padding: 0.06in 0.08in; }
.kpi-tile {
  border: 1px solid var(--color-border); border-radius: var(--radius);
  background: var(--color-surface); padding: 0.08in 0.08in 0.085in;
  box-shadow: var(--shadow);
  text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 0.045in;
}
.kpi-label {
  /* Larger and boxed to a narrow measure so it wraps to two lines as the mockup does —
     "TARGETED / IMPRESSIONS" — which is what makes a KPI read as a titled figure. */
  font-size: var(--size-sm); font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.02em; color: var(--color-value); line-height: 1.18;
  max-width: 1.55in; min-height: 2.1em; display: flex; align-items: center; justify-content: center;
}
.icon-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 0.38in; height: 0.38in; border-radius: 50%;
  background: #E3EEFB; color: var(--color-icon);
}
/* Raster icon marks carry their own art, so a badge/ring behind one is chrome that fights the
   illustration — drop the disc, border and fixed box and let the image sit on its own. */
.report-icon-img { display: inline-block; object-fit: contain; vertical-align: middle; }
.icon-badge:has(.report-icon-img) {
  width: auto; height: auto; border-radius: 0; background: none; border: none;
}
.kpi-value {
  font-size: var(--size-xl); font-weight: 700; color: #14181F;
  margin-top: auto; letter-spacing: -0.01em;
}

/*
 * Block: DeliveryStrip — the contracted figures.
 *
 * A blue rule around it, not the neutral card border. In the mockup this is the one
 * panel that is outlined in the accent colour, which is what marks it as the contract
 * rather than another block of delivery numbers.
 */
.strip {
  display: flex; border: 1.5px solid var(--color-strip-border);
  border-radius: var(--radius); background: var(--color-surface);
  padding: 0.03in 0; box-shadow: var(--shadow);
}
.strip > div {
  flex: 1; padding: 0.07in 0.1in; border-left: 1px solid #E3E8EF; text-align: center;
}
.strip > div:first-child { border-left: none; }
/* V2: a slightly taller contract strip, its figure + label centred vertically in each cell. */
.strip-v2 > div {
  padding-top: 0.11in; padding-bottom: 0.11in;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
}
/* A little air between each contract figure and its label. */
.strip-v2 .strip-label { margin-top: 0.06in; }
.strip-value { font-size: var(--size-md); font-weight: 700; color: #14181F; }
/* The contract labels (Contracted Impressions, Over-Delivered, …) read extra-bold under their figures. */
.strip-label { font-size: var(--size-xs); color: var(--color-value); font-weight: 800; margin-top: 0.02in; }
/* Contracted / Actual: an eye badge beside the value, as the DOOH mockup has it. */
.cva-value { display: flex; align-items: center; justify-content: center; gap: 0.09in; }
/* The geo card's ZIP band — "ZIP" label beside the codes, on a light row, as the mockup sets it. */
.zip-row {
  display: flex; gap: 0.14in; align-items: baseline; margin-top: 0.07in;
  background: #F3F5F8; border-radius: 4px; padding: 0.06in 0.1in; box-shadow: var(--shadow);
}
.zip-label { color: var(--color-primary); font-weight: 700; letter-spacing: 0.04em; flex: 0 0 auto; }
.zip-values { font-size: var(--size-xs); color: #2B3038; }

/* Block: CampaignMetaCard — icon, label, value, hairline between rows */
.meta-rows { margin: 0; }
.meta-row {
  display: flex; align-items: center; gap: 0.11in;
  padding: 0.088in 0; border-bottom: 1px solid #E7EAEF;
}
.meta-row:last-child { border-bottom: none; }
.meta-row dt {
  font-weight: 700; color: var(--color-value); text-transform: uppercase;
  letter-spacing: 0.02em; font-size: var(--size-xs); flex: 0 0 auto;
}
.meta-row dd { margin: 0; color: #2B3038; font-size: var(--size-sm); }
/* V2: the values (client, dates, IO) are bold like their labels. */
.meta-card-v2 .meta-row dd { font-weight: 700; font-size: var(--size-xs); }
/*
 * The campaign-details badge is an outlined ring on white, not a filled disc.
 *
 * The KPI tiles use the filled treatment and these rows use the ring; they are different
 * marks doing different jobs in the mockup, and making both filled loses that.
 */
.icon-badge.ring {
  width: 0.3in; height: 0.3in; flex: 0 0 auto;
  background: transparent; border: 1.4px solid var(--color-value);
}
/* A raster mark carries its own badge art, so the outlined ring around it is a double outline.
   This beats the .icon-badge.ring border above (equal specificity, later) and only matches a V2
   image: a legacy ring holds an svg, never .report-icon-img, so it keeps its outline. */
.icon-badge.ring:has(.report-icon-img) {
  width: auto; height: auto; background: none; border: none;
}
.meta-note { margin: 0.1in 0 0; font-size: var(--size-xs); line-height: 1.5; color: #2B3038; }
.creative-caption { margin: 0.06in 0 0; font-size: var(--size-xs); color: #6A727E; }
/* The identity creative floating on the page — no card frame, background or shadow; just the artwork. */
.creative-bare { display: flex; flex-direction: column; align-items: center; justify-content: center; }
/* A lead line — the geo summary reads as a statement, not a caption. */
.lead { font-size: var(--size-md); font-weight: 700; color: var(--color-value); margin: 0; }
/*
 * Long lists set as a paragraph — the mockup's music-genres block runs to forty-two
 * entries and fits in five lines only because it is set small and tight.
 */
.small-copy { font-size: var(--size-xs); line-height: 1.5; margin: 0; }

/* Block: DeviceCategoryCard — illustration left, the split right */
.device-cat { display: flex; align-items: center; gap: 0.14in; }
.device-art { color: #35507F; flex: 0 0 auto; }
/*
 * Two aligned columns at the right of the card: labels in one, figures in the other. "Mobile" and
 * "Desktop" line up with each other, the numbers line up on their right edge (tabular digits), and a
 * clear gap separates the two. The row wrappers are display:contents so dt/dd sit directly on the grid.
 */
.device-split {
  margin: 0; flex: 1 1 auto; padding-right: 0.06in;
  display: grid; grid-template-columns: max-content max-content; justify-content: end;
  column-gap: 0.3in; row-gap: 0.1in; align-items: baseline;
}
.device-split > div { display: contents; }
/* The label matches its number — bold, dark blue — so "Mobile" reads as paired with its figure. */
.device-split dt { color: var(--color-value); font-weight: 700; text-align: left; }
.device-split dd {
  margin: 0; font-size: var(--size-md); font-weight: 700; color: var(--color-value);
  text-align: right; font-variant-numeric: tabular-nums;
}
/* Device Details list — device name and count both bold / dark blue, matching the other breakdown cards. */
/* Tighter vertical padding than a general table: a full device list then fits the fixed summary page
   with clear space before the footer (the audio summary has no room to spare). */
.breakdown-card td { font-weight: 700; color: var(--color-value); padding-top: 0.026in; padding-bottom: 0.026in; }

/* Block: CampaignChannelsCard — icon above label, divided */
.channel-row { display: flex; align-items: stretch; }
.channel-cell {
  flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.05in;
  /* A visible vertical divider between channels, inset from the card edges as the mockup has it. Tight
     vertical padding so the channel mark can fill the card instead of the card growing around it. */
  padding: 0.02in 0.08in; margin: 0.03in 0; border-left: 1px solid #C7D0DD; color: var(--color-value);
  text-align: center;
}
.channel-cell:first-child { border-left: none; }
/*
 * V2: the device-split and channel cards are sized by their marks, so trim the padding around
 * them (not the marks) to make the two cards shorter. No min-height floor on these two either — they
 * are as tall as their content; the floor still evens out the Device Details / Browser Type row.
 */
.breakdown-card:has(> .device-cat), .breakdown-card:has(> .channel-row) { min-height: 0; }
.breakdown-card > .device-cat { padding-top: 0.08in; padding-bottom: 0.08in; }
.breakdown-card > .channel-row { padding-top: 0.06in; padding-bottom: 0.06in; }
.breakdown-card .channel-cell { padding: 0 0.08in; margin: 0; gap: 0.04in; }
/* The channel marks carry transparent margin below the art, so the label is pulled up to sit closer to it. */
.breakdown-card .channel-label { margin-top: -0.07in; }
/* No side padding on the channel row: each channel then centres in its half of the card, not in a half
   inset by the body padding (which pulled both marks toward the divider). */
.breakdown-card > .channel-row { padding-left: 0; padding-right: 0; }
/* The illustration + Mobile/Desktop figures sit as one group centred in the card body, both ways: equal
   space left and right, and (the body fills the card's stretched height) equal space above and below. */
.breakdown-card > .device-cat {
  flex: 1 1 auto; justify-content: center; align-items: center; gap: 0.55in; padding-left: 0; padding-right: 0;
}
.breakdown-card > .device-cat > .device-art { flex: 0 0 auto; display: flex; }
.breakdown-card > .device-cat > .device-split { flex: 0 0 auto; padding-right: 0; }
/* Summary row spacing — added to the stack gap above each row; 0 unless the template sets it. */
[data-block-id="kpis"], [data-block-id="delivery"],
[data-block-id="breakdown-top"], [data-block-id="breakdown-bottom"] { margin-top: var(--summary-row-space, 0in); }
[data-block-id="listening"], [data-block-id="demographics"],
[data-block-id="genres-card"] { margin-top: var(--audio-row-space, 0in); }
/* Padding, not margin, so the paginator's measured block height includes the space. */
[data-block-id="details-card"], [data-block-id="companion-details-card"] { padding-top: var(--details-top-space, 0in); }
.channel-label { font-size: var(--size-xs); color: var(--color-value); font-weight: 700; }

/* Block: CreativeDetailList — one bordered card per creative, artwork beside the facts */
/* Spacing tuned so five creative cards fit one Campaign Details page (the paginator counts this gap). */
.creative-list { display: flex; flex-direction: column; gap: 0.12in; }
.creative-item {
  display: flex; align-items: center; gap: 0.18in;
  border: 1px solid var(--color-border); border-radius: var(--radius);
  background: var(--color-surface); padding: 0.14in 0.18in;
  box-shadow: var(--shadow);
  break-inside: avoid; page-break-inside: avoid;
}
.creative-art {
  flex: 0 0 2.35in; display: flex; align-items: center; justify-content: center;
  border-right: 1px solid #E4E8EE; padding-right: 0.14in; align-self: stretch;
}
.creative-facts { margin: 0; flex: 1 1 auto; }
.creative-facts > div { display: flex; gap: 0.08in; padding: 0.02in 0; align-items: baseline; }
.creative-facts > div.stacked { display: block; }
.creative-facts dt { font-weight: 700; color: var(--color-value); font-size: var(--size-xs); }
/* Values (dimensions, clicks) bold / dark blue to match their labels — the same treatment through the report. */
.creative-facts dd { margin: 0; color: var(--color-value); font-weight: 700; }
.creative-facts dd.url { font-size: var(--size-xs); color: #4A515C; word-break: break-all; font-weight: 400; }
/* CreativeDetailList icons variant — facts as ring-badge rows (the audio summary identity block) */
.creative-item-icons { align-items: stretch; }
.creative-item-icons .creative-art { flex: 0 0 2.9in; }
.creative-facts-icons { margin: 0; flex: 1 1 auto; align-self: center; }
.creative-facts-icons .meta-row dt { text-transform: none; letter-spacing: 0.01em; color: var(--color-value); }
.creative-facts-icons .meta-row dd { font-size: var(--size-sm); }
.creative-facts-icons .meta-row dd.url { font-size: var(--size-xs); color: #4A515C; word-break: break-all; }

/* AttributeCards header variant — a dark heading bar over a centred value (audio demographics) */
.attr-card { border: 1px solid var(--color-border); border-radius: var(--radius); background: var(--color-surface); overflow: hidden; box-shadow: var(--shadow); }
.attr-value { text-align: center; padding: 0.16in 0.1in; font-size: var(--size-md); font-weight: 700; color: var(--color-value); }

/* Block: BrowserTypeCard — marks in a row, count beneath */
/* Centre the marks vertically in a card stretched tall by its taller sibling (Device Details). */
.browser-body { flex: 1 1 auto; display: flex; flex-direction: column; justify-content: center; }
.browser-row { display: flex; justify-content: space-around; align-items: flex-end; gap: 0.06in; }
.browser-cell { display: flex; flex-direction: column; align-items: center; gap: 0.05in; }
.browser-count { font-size: var(--size-sm); font-weight: 700; color: #14181F; }

/* Tables */
table { width: 100%; border-collapse: collapse; font-size: var(--size-xs); }
th { text-align: left; font-weight: 700; padding: 0.05in 0.06in; border-bottom: 1px solid var(--color-border); }
td { padding: 0.045in 0.06in; border-bottom: 1px solid var(--color-border); }
tr:last-child td { border-bottom: none; }
td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
.zebra tbody tr:nth-child(even) { background: var(--color-surface-muted); }

/* Placeholders for assets the M1 pipeline does not resolve yet */
.asset-placeholder {
  border: 1px dashed var(--color-border);
  background: var(--color-surface-muted);
  color: #8A93A0;
  display: flex; align-items: center; justify-content: center;
  font-size: var(--size-xs);
}

/* Top Sites: no bullets, zebra rows — the mockup sets it as a striped two-column table. */
.list-cols { column-gap: 0.2in; list-style: none; padding-left: 0; }
.list-cols li { break-inside: avoid; padding: 0.035in 0.07in; font-size: var(--size-xs); color: #2B3038; }
.list-cols li:nth-child(even) { background: #F3F5F8; }
ul.plain { margin: 0; padding-left: 0.16in; }

.provenance {
  border-left: 3px solid var(--color-accent);
  background: var(--color-surface-muted);
  padding: 0.08in 0.12in; font-size: var(--size-xs);
}
`.trim();
